import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './CasefilePrivacySettings.css';

const CasefilePrivacySettings = ({ casefile, isOwner, onUpdate }) => {
  const [isPublic, setIsPublic] = useState(casefile?.isPublic || false);
  const [showName, setShowName] = useState(casefile?.showNameToViewers || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Don't show if not owner
  if (!isOwner) return null;

  // Check if privacy is locked (has sales)
  const isLocked = casefile?.privacyLocked || false;

  const handleSavePrivacy = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const casefileRef = doc(db, 'casefiles', casefile.id);
      const updates = {
        isPublic: isPublic,
        showNameToViewers: showName,
        updatedAt: new Date()
      };

      await updateDoc(casefileRef, updates);
      
      setSuccessMessage('✅ Privacy settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Notify parent component to refresh data
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error saving privacy settings:', err);
      setError('Failed to save privacy settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Check if there are unsaved changes
  const hasChanges = isPublic !== casefile?.isPublic || 
                     showName !== casefile?.showNameToViewers;

  return (
    <div className="privacy-settings-panel">
      <h3>🔐 Privacy Settings</h3>
      
      {isLocked && (
        <div className="locked-notice">
          ⚠️ <strong>Privacy Locked:</strong> Jobs have been sold from this casefile. 
          Privacy mode cannot be changed to protect buyers.
        </div>
      )}

      {/* Privacy Mode Toggle */}
      <div className="setting-group">
        <label htmlFor="privacy-mode">
          <strong>Casefile Visibility:</strong>
        </label>
        <select 
          id="privacy-mode"
          value={isPublic ? 'public' : 'private'} 
          onChange={(e) => setIsPublic(e.target.value === 'public')}
          disabled={isLocked || saving}
          className="privacy-select"
        >
          <option value="private">🔒 Private (Only You)</option>
          <option value="public">🌐 Public (Everyone)</option>
        </select>
      </div>

      {/* Name Visibility (only for private casefiles) */}
      {!isPublic && (
        <div className="setting-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showName}
              onChange={(e) => setShowName(e.target.checked)}
              disabled={saving}
            />
            <span>Show casefile name to viewers who purchase jobs</span>
          </label>
          <p className="help-text">
            If unchecked, buyers will see "Private Investigation" instead of "{casefile?.title}"
          </p>
        </div>
      )}

      {/* Explanation Box */}
      <div className="privacy-explanation">
        {isPublic ? (
          <>
            <h4>🌐 Public Casefile</h4>
            <ul>
              <li>✓ Anyone can see this casefile exists</li>
              <li>✓ Anyone can view the casefile name and description</li>
              <li>✓ All evidence videos are FREE to watch (community resource)</li>
              <li>✓ Jobs can still be private or for sale</li>
              <li>✓ Great for community safety and collaboration</li>
            </ul>
          </>
        ) : (
          <>
            <h4>🔒 Private Casefile</h4>
            <ul>
              <li>✓ Only you can see this casefile exists</li>
              <li>✓ Evidence videos are hidden from others</li>
              <li>✓ Jobs can be listed for sale in marketplace (future)</li>
              <li>✓ Buyers get access to specific job + video only</li>
              <li>✓ Better for sensitive investigations</li>
            </ul>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Save Button */}
      <button 
        onClick={handleSavePrivacy}
        disabled={saving || isLocked || !hasChanges}
        className={`save-privacy-btn ${hasChanges ? 'has-changes' : ''}`}
      >
        {saving ? '💾 Saving...' : hasChanges ? '💾 Save Privacy Settings' : '✓ No Changes'}
      </button>

      {/* Warning for switching to public */}
      {isPublic && !casefile?.isPublic && hasChanges && (
        <div className="warning-message">
          ⚠️ <strong>Warning:</strong> Switching to public will make this casefile and all 
          evidence videos visible to everyone. This action can be reversed.
        </div>
      )}
    </div>
  );
};

export default CasefilePrivacySettings;

